import {useState, useEffect} from "react";
import Axios from "axios";
import {saveAs} from "file-saver";
import {useTranslation} from "react-i18next";
import {Form, Row, Col, Input, Button, Tag, Typography, Space, DatePicker} from "antd";
import {errorHandler, SelectProject, TableRender, xaMessage} from "../../common";
import {jsonToCSV} from "../../utils/stringHelper";
import "antd/dist/antd.css";

const {RangePicker} = DatePicker;

/* Main Component */
const ServerCheck = () => {
  const [isExecutionAuth, setIsExecutionAuth] = useState(false);
  const [projectList, setProjectList] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [apiStatusList, setApiStatusList] = useState([]);
  const [isShowLoading, setIsShowLoading] = useState(false);
  const [form] = Form.useForm();

  /** i18next */
  const {t} = useTranslation();

  const columns = [
    {
      title: "API",
      dataIndex: "api",
      width: 200,
    },
    {
      title: "업무 명칭",
      dataIndex: "name",
      width: 250,
    },
    {
      title: "상태",
      dataIndex: "status",
      width: 100,
      render: (status) => (
        <>
          <StatusDot status={status} size={10} />
        </>
      ),
    },
    {
      title: "호출 소요 시간 (초)",
      dataIndex: "duration",
      width: 150,
      render: (duration) => (duration ? parseFloat(duration) / 1000 : "-"),
    },
  ];

  const handleFetchProjectList = async () => {
    try {
      const {data} = await Axios.get("/common/project/list");
      setProjectList(data.filter((item) => item.project_mng_sq !== 0));
    } catch (err) {
      errorHandler(t("message_error_beginning"), err);
    }
  };

  const handleFetchApiStatusList = async () => {
    const {password, selectedDate, coreUserId, coreUserPw} = form.getFieldsValue();

    const userId = JSON.parse(sessionStorage.getItem("userInfo")).user_email_id;

    if (!userId || !coreUserId || !selectedProject || !password || !coreUserPw || !selectedDate) {
      xaMessage("warn", "입력 항목 확인");
      return;
    }

    const startDt = selectedDate[0].format("YYYY-MM-DD");
    const endDt = selectedDate[1].format("YYYY-MM-DD");

    try {
      setIsShowLoading(true);
      const {data} = await Axios.post("/system/check", {
        user_id: userId,
        user_pw: password,
        core_user_id: coreUserId,
        core_user_pw: coreUserPw,
        project_id: selectedProject,
        start_dt: startDt,
        end_dt: endDt,
      });

      if (data.error && data.error.includes("logined")) {
        errorHandler(t("message_error_beginning"), t("xc_system_check_core_logined"), null);
      } else if (data.length > 0) {
        const indexedData = data.map((item, index) => ({
          ...item,
          index: index + 1,
        }));
        setApiStatusList(indexedData);
      }
      setIsShowLoading(false);
    } catch (err) {
      console.log(err);
      errorHandler(t("message_error_beginning"), t("message_search_failed"), null);
      setIsShowLoading(false);
    }
  };

  const handleDownloadCSV = () => {
    if (apiStatusList.length === 0) {
      xaMessage("error", "확인 내용 없음!");
      return;
    }

    const fileName = `system_check_result.csv`;
    const csvString = jsonToCSV(
      apiStatusList.map((item) => ({
        API: item.api,
        "업무 명칭": item.name,
        상태: item.status ? "정상" : "확인필요",
        "호출 소요 시간 (초)": item.duration ? parseFloat(item.duration) / 1000 : "-",
      }))
    );

    return saveAs(new Blob(["\uFEFF" + csvString], {type: "text/csv;charset=utf-8;"}), fileName);
  };

  const handleOnChangeProject = (value) => {
    setSelectedProject(value);
  };

  useEffect(() => {
    const loginInfo = sessionStorage.getItem("userInfo");

    if (!loginInfo) {
      setIsExecutionAuth(false);
      xaMessage("error", "로그인 하시오!");

      return;
    }

    setIsExecutionAuth(true);
    handleFetchProjectList();
  }, []);

  /* Renderer */
  return (
    <>
      {/* 조회 조건 영역 */}
      <div className="search-area common-search-area-form">
        <Row className="search-area-row-1">
          <Col span={8} key={0} className="search-area-column">
            <SelectProject
              nameSpace={t("general_project")}
              onChange={handleOnChangeProject}
              value={selectedProject}
              selectList={projectList}
            />
          </Col>
        </Row>
        <Form form={form}>
          <Row className="search-area-row-2">
            <Col span={6} key={1}>
              <Form.Item name="password" label="암호">
                <Input.Password style={{width: 150, height: 35}} />
              </Form.Item>{" "}
            </Col>
            <Col span={6} key={2}>
              <Form.Item name="selectedDate" label="일자">
                <RangePicker name="date" size="small" style={{width: 220, height: 35}} />
              </Form.Item>
            </Col>
            <Col span={6} key={3}>
              <Form.Item name="coreUserId" label="Core ID">
                <Input style={{width: 200, height: 35}} />
              </Form.Item>{" "}
            </Col>
            <Col span={6} key={4}>
              <Form.Item name="coreUserPw" label="Core 암호">
                <Input.Password style={{width: 150, height: 35}} />
              </Form.Item>{" "}
            </Col>
          </Row>
        </Form>
        <Row className="search-button-area">
          <Button type="primary" onClick={handleFetchApiStatusList} disabled={!isExecutionAuth}>
            점검 시작
          </Button>
          <Button
            className="button-margin-left"
            type="default"
            onClick={handleDownloadCSV}
            disabled={apiStatusList.length === 0}
          >
            {t("button_download_csv")}
          </Button>
        </Row>
      </div>
      <TableRender
        columns={columns}
        dataSource={apiStatusList}
        isLoading={isShowLoading}
        pagination={false}
        rowKey="index"
      />
    </>
  );
};

const StatusDot = ({status, size = 10}) => {
  const color = status ? "#52c41a" : "#ff4d4f";
  const text = status ? "정상" : "확인필요";

  return (
    <Space size={8}>
      {!status && (
        <Tag
          bordered={false}
          style={{
            width: size,
            height: size,
            minWidth: size,
            paddingInline: 0,
            paddingBlock: 0,
            borderRadius: 999,
            background: color,
            lineHeight: `${size}px`,
          }}
        />
      )}
      {text ? <Typography.Text>{text}</Typography.Text> : null}
    </Space>
  );
};

export default ServerCheck;
